import { Injectable } from "@angular/core";
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
  HttpErrorResponse,
} from "@angular/common/http";
import { BehaviorSubject, Observable, throwError } from "rxjs";
import { catchError, filter, switchMap, take } from "rxjs/operators";
import { Router } from "@angular/router";
import { MessageService } from "primeng/api";
import { environment } from "../../environments/environment";
import { UserManagementService } from "../user-management/user-management.service";

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private isLoggingOut = false;
  // Refresh coordination: ensures only one refresh call runs while concurrent
  // 401s queue and replay once a new access token is available. The subject
  // publishes either the new token or the refresh failure so queued requests
  // never hang.
  private isRefreshing = false;
  private refreshTokenSubject = new BehaviorSubject<{
    token?: string;
    error?: HttpErrorResponse;
  } | null>(null);
  private readonly LOGOUT_DEBUG_KEY = "logout_debug_last";
  private readonly publicApiPaths = [
    "/login",
    "/signup",
    "/signup/confirm",
    "/username/availability",
    "/forgotpass",
    "/forgot-password",
    "/resetPassword",
    "/confirmPassword",
    "/confirmsignup",
    "/isuseravailable",
    "/isusernameavailable",
    // SSO ticket redeem: unauthenticated by design. Listing it here stops a
    // stale Bearer token being attached, and stops a 401 (expired/used ticket)
    // from triggering a token refresh + "Session expired" logout.
    "/sso/redeem",
  ];
  private readonly protectedBaseUrls: string[] = [
    environment.nodeUrl1,
    environment.nodeUrl2,
    environment.baseUri,
    environment.requestUri,
    environment.MICROSERVICE_URL,
    environment.NEW_AUTH,
  ]
    .filter(Boolean)
    .map((url) => this.normalizeUrl(url));

  constructor(
    private router: Router,
    private messageService: MessageService,
    private userService: UserManagementService
  ) {}

  intercept(
    request: HttpRequest<unknown>,
    next: HttpHandler
  ): Observable<HttpEvent<unknown>> {
    const token = this.getAuthToken();
    const url = request.url || "";
    const isDmsEndpoint = this.isDmsEndpoint(url);
    const isOurApi = this.shouldAttachToken(url);
    const isPublic = this.isPublicApiEndpoint(url);

    // A login attempt after a forced logout starts a fresh auth lifecycle:
    // re-arm the interceptor (the SPA is not always fully reloaded on re-login,
    // and a stale isLoggingOut=true would silently skip refresh-and-retry).
    if (isPublic) {
      this.isLoggingOut = false;
    }

    let clonedRequest = request;

    if (isDmsEndpoint) {
      // DMS is a separate service (backenddms.pure.bi) authenticated with its own
      // static key. NOTE: moving this key server-side is tracked as a backend item.
      clonedRequest = request.clone({
        headers: request.headers.set(
          "x-api-key",
          "PUREBI-MOBL-PROD-21121989-X8F9A7C3"
        ),
      });
    } else if (isOurApi) {
      // Both-mode auth: send the httpOnly session/refresh cookies (withCredentials)
      // and keep the Bearer header for backward compatibility until cookies are
      // verified in staging. withCredentials is also required on the public auth
      // endpoints (login/refresh) so the browser will store the Set-Cookie response.
      const attachBearer =
        !!token && !isPublic && !request.headers.has("Authorization");
      clonedRequest = request.clone({
        ...(attachBearer
          ? { setHeaders: { Authorization: `Bearer ${token}` } }
          : {}),
        withCredentials: true,
      });
    }

    return next.handle(clonedRequest).pipe(
      catchError((error: HttpErrorResponse) => {
        if (error.status === 401) {
          const failedUrl = error.url || request.url || "";
          const isAuthEndpoint = this.isPublicApiEndpoint(failedUrl);
          const shouldSuppressAutoLogout =
            this.shouldSuppressAutoLogoutForRequest(failedUrl);

          console.log("[AuthInterceptor] 401 decision", {
            failedUrl,
            isAuthEndpoint,
            shouldSuppressAutoLogout,
            isRefreshing: this.isRefreshing,
            isLoggingOut: this.isLoggingOut,
          });

          if (shouldSuppressAutoLogout) {
            this.recordLogoutDebug("401_suppressed", {
              failedUrl,
              route: this.router.url,
              isAuthEndpoint,
            });
            this.messageService.add({
              severity: "warn",
              summary: "Save failed",
              detail:
                "The request was not authorized, but your current dashboard form is still open. Please retry after reactivating your session.",
            });
            return throwError(() => error);
          }

          // Public auth endpoints (login, forgot-password, …): let the component
          // surface the error message; never refresh or auto-logout here.
          if (isAuthEndpoint) {
            return throwError(() => error);
          }

          // A 401 from auth/refresh itself means the session is dead
          // (NO_REFRESH_TOKEN | REFRESH_TOKEN_INVALID | SESSION_INVALID) —
          // this is the ONLY automatic logout.
          if (this.isRefreshEndpoint(failedUrl)) {
            return this.forceLogout(error, failedUrl, isAuthEndpoint);
          }

          // DMS authenticates with a static x-api-key, not the user's session:
          // its 401 says nothing about our session and can't be fixed by a
          // token refresh -> surface the error without ending the session.
          if (isDmsEndpoint) {
            return throwError(() => error);
          }

          // Eligible protected request: try a one-shot token refresh + replay
          // before tearing down the session.
          if (!this.isLoggingOut) {
            return this.handle401WithRefresh(request, next, error);
          }
        }

        return throwError(() => error);
      })
    );
  }

  /**
   * Attempt to refresh the access token and replay the original request.
   * Concurrent 401s queue on refreshTokenSubject and replay with the new token
   * (or receive the refresh failure so they never hang).
   *
   * Logout policy: the ONLY automatic logout is a 401 from auth/refresh itself
   * (dead session: NO_REFRESH_TOKEN | REFRESH_TOKEN_INVALID | SESSION_INVALID).
   * Any other refresh failure (429 throttle, network, 5xx) keeps the session:
   * the original request fails and a later 401 will retry the refresh.
   */
  private handle401WithRefresh(
    request: HttpRequest<unknown>,
    next: HttpHandler,
    originalError: HttpErrorResponse
  ): Observable<HttpEvent<unknown>> {
    if (this.isRefreshing) {
      // Wait for the in-flight refresh to publish its outcome, then replay
      // with the new token — or propagate the refresh failure.
      return this.refreshTokenSubject.pipe(
        filter((result) => result !== null),
        take(1),
        switchMap((result) =>
          result!.token
            ? next.handle(this.attachToken(request, result!.token))
            : throwError(() => result!.error ?? originalError)
        )
      );
    }

    this.isRefreshing = true;
    this.refreshTokenSubject.next(null);

    return this.userService.refreshAccessToken().pipe(
      switchMap((res: any) => {
        const newToken = res?.access_token;
        if (res?.status === 1 && newToken) {
          // Stores the rotated refresh_token and schedules the next
          // proactive refresh (sliding window).
          this.userService.storeRefreshedTokens(res);
          this.isRefreshing = false;
          this.refreshTokenSubject.next({ token: newToken });
          return next.handle(this.attachToken(request, newToken));
        }
        // 200 without a usable token shouldn't happen per the backend
        // contract; treat as transient — do NOT tear the session down.
        this.isRefreshing = false;
        this.refreshTokenSubject.next({ error: originalError });
        return throwError(() => originalError);
      }),
      catchError((refreshError: HttpErrorResponse) => {
        this.isRefreshing = false;
        this.refreshTokenSubject.next({ error: refreshError });
        if (refreshError?.status === 401) {
          // Dead session. (The refresh request's own interceptor pass has
          // usually forced logout already; this is the idempotent fallback.)
          return this.forceLogout(originalError, request.url, false);
        }
        // 429 / network / 5xx: keep the session, surface the original error.
        return throwError(() => originalError);
      })
    );
  }

  private attachToken(
    request: HttpRequest<unknown>,
    token: string
  ): HttpRequest<unknown> {
    return request.clone({
      setHeaders: { Authorization: `Bearer ${token}` },
      withCredentials: true,
    });
  }

  /** Tear down the current session and redirect to login (existing behaviour). */
  private forceLogout(
    error: HttpErrorResponse,
    failedUrl: string,
    isAuthEndpoint: boolean
  ): Observable<never> {
    if (this.isLoggingOut) {
      return throwError(() => error);
    }

    this.recordLogoutDebug("401_logout", {
      failedUrl,
      route: this.router.url,
      isAuthEndpoint,
    });

    this.isLoggingOut = true;
    this.callLogoutAPI();
    this.userService.clearCurrentTabSession();
    this.clearNotificationStorage();
    localStorage.removeItem("notification_theme");
    localStorage.removeItem("uploadedpath");

    return this.handleSessionExpired(error);
  }

  private isWithinLoginGracePeriod(): boolean {
    const loginTimestamp = sessionStorage.getItem("loginTimestamp");
    if (!loginTimestamp) {
      return false;
    }

    const loginTime = parseInt(loginTimestamp, 10);
    const now = Date.now();
    const timeSinceLogin = now - loginTime;
    const gracePeriod = 5000;

    if (timeSinceLogin < gracePeriod) {
      console.log(
        `Within login grace period (${Math.round(
          timeSinceLogin
        )}ms < ${gracePeriod}ms), skipping logout API call`
      );
      return true;
    }

    sessionStorage.removeItem("loginTimestamp");
    return false;
  }

  private callLogoutAPI(): void {
    try {
      if (this.isWithinLoginGracePeriod()) {
        console.log("Skipping logout API call - within login grace period");
        return;
      }

      const token = this.getAuthToken();
      const logoutUrl = `${environment.nodeUrl1}auth/logout`;
      console.log("callLogoutAPI: Calling logout API:", logoutUrl);

      fetch(logoutUrl, {
        method: "POST",
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          "Content-Type": "application/json",
        },
        // Send cookies so the backend can clear the httpOnly session/refresh cookies.
        credentials: "include",
        keepalive: true,
      })
        .then((response) => {
          console.log("Logout API called successfully", {
            status: response.status,
            ok: response.ok,
          });
        })
        .catch((err) => {
          console.error(
            "Logout API call failed (expected if session expired):",
            err
          );
        });
    } catch (error) {
      console.error("Error calling logout API:", error);
    }
  }

  private clearNotificationStorage(): void {
    try {
      const keysToRemove: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith("app_notifications_")) {
          keysToRemove.push(key);
        }
      }

      keysToRemove.forEach((key) => {
        localStorage.removeItem(key);
      });

      if (keysToRemove.length > 0) {
        console.log(
          `401 Logout cleanup: Cleared ${keysToRemove.length} notification keys`
        );
      }
    } catch (e) {
      console.error("Error clearing notification storage during 401 logout:", e);
    }
  }

  private handleSessionExpired(error: HttpErrorResponse): Observable<never> {
    this.messageService.add({
      severity: "error",
      summary: "Session expired. Please login again.",
      detail: "Your session has expired. Please login again to continue.",
    });

    this.router.navigate(["/login"]);
    return throwError(() => error);
  }

  private getAuthToken(): string | null {
    return this.userService.getAuthToken();
  }

  private normalizeUrl(url: string): string {
    return url.replace(/\/+$/, "").toLowerCase();
  }

  private shouldAttachToken(requestUrl: string): boolean {
    if (!requestUrl) {
      return false;
    }

    if (!/^https?:\/\//i.test(requestUrl)) {
      return true;
    }

    const normalizedRequestUrl = requestUrl.toLowerCase();
    return this.protectedBaseUrls.some((baseUrl) => {
      if (!baseUrl) {
        return false;
      }

      return (
        normalizedRequestUrl.startsWith(`${baseUrl}/`) ||
        normalizedRequestUrl === baseUrl
      );
    });
  }

  private isPublicApiEndpoint(requestUrl: string): boolean {
    // Match on the URL path only (ignore query string / hash) and require the
    // public path to be a trailing path segment. A naive substring check would
    // misclassify protected endpoints whose path merely contains an auth
    // keyword — e.g. "/me/login-history" contains "/login", which previously
    // caused the Bearer token to be stripped and the request to 401.
    const path = (requestUrl || "").toLowerCase().split("?")[0].split("#")[0];
    return this.publicApiPaths.some(
      (publicPath) => path === publicPath || path.endsWith(publicPath)
    );
  }

  private isRefreshEndpoint(requestUrl: string): boolean {
    return (requestUrl || "").toLowerCase().includes("auth/refresh");
  }

  private isDmsEndpoint(requestUrl: string): boolean {
    const dmsUrl = environment.dmsUrl
      ? this.normalizeUrl(environment.dmsUrl)
      : "";
    const normalizedRequestUrl = requestUrl.toLowerCase();

    return (
      normalizedRequestUrl.includes("backenddms.pure.bi") ||
      (!!dmsUrl && normalizedRequestUrl.startsWith(dmsUrl))
    );
  }

  private shouldSuppressAutoLogoutForRequest(requestUrl: string): boolean {
    if (!requestUrl) {
      return false;
    }

    const normalizedRequestUrl = requestUrl.toLowerCase();
    const currentRoute = (this.router.url || "").toLowerCase();
    const isDashboardAuthoringRoute =
      currentRoute.includes("/report-system/add-dashboard") ||
      currentRoute.includes("/report-system/edit-dashboard") ||
      currentRoute.includes("/report-system/dashboard-report");

    if (!isDashboardAuthoringRoute) {
      return false;
    }

    const normalizedMicroserviceUrl = this.normalizeUrl(
      environment.MICROSERVICE_URL || ""
    );

    if (!normalizedMicroserviceUrl) {
      return false;
    }

    const suppressedPrefixes = [
      `${normalizedMicroserviceUrl}/dashboard-groups`,
      `${normalizedMicroserviceUrl}/dashboards/update-in-group`,
      `${normalizedMicroserviceUrl}/reportdetails/`,
    ];

    return suppressedPrefixes.some((prefix) =>
      normalizedRequestUrl.startsWith(prefix)
    );
  }

  private recordLogoutDebug(
    source: string,
    details: Record<string, unknown>
  ): void {
    const payload = {
      source,
      timestamp: new Date().toISOString(),
      route: this.router.url,
      details,
    };

    console.log("[LogoutDebug][AuthInterceptor]", payload);

    try {
      localStorage.setItem(this.LOGOUT_DEBUG_KEY, JSON.stringify(payload));
    } catch (error) {
      console.error("Failed to persist logout debug info", error);
    }
  }
}
